import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import "./BusinessBotView.css";

function BusinessBotView({ bot, palette = "midnight", onVote, onDashboard }) {
  const { t, i18n } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const palettes = {
    midnight: {
      "--primary": "#6366f1",
      "--secondary": "#a855f7",
      "--primary-rgb": "99, 102, 241",
      "--bg-deep": "#020617",
      "--glass-bg": "rgba(15, 23, 42, 0.65)",
      "--glass-border": "rgba(99, 102, 241, 0.3)",
      "--text-main": "#f8fafc",
      "--accent-glow": "rgba(168, 85, 247, 0.4)",
      "--surface-2": "#1e293b",
    },
    sunset: {
      "--primary": "#f59e0b",
      "--secondary": "#ef4444",
      "--primary-rgb": "245, 158, 11",
      "--bg-deep": "#0c0a09",
      "--glass-bg": "rgba(28, 25, 23, 0.7)",
      "--glass-border": "rgba(245, 158, 11, 0.3)",
      "--text-main": "#fafaf9",
      "--accent-glow": "rgba(239, 68, 68, 0.4)",
      "--surface-2": "#292524",
    },
    neon: {
      "--primary": "#10b981",
      "--secondary": "#3b82f6",
      "--primary-rgb": "16, 185, 129",
      "--bg-deep": "#09090b",
      "--glass-bg": "rgba(9, 9, 11, 0.8)",
      "--glass-border": "rgba(16, 185, 129, 0.3)",
      "--text-main": "#f0f9ff",
      "--accent-glow": "rgba(59, 130, 246, 0.4)",
      "--surface-2": "#18181b",
    },
    royal: {
      "--primary": "#e11d48",
      "--secondary": "#c026d3",
      "--primary-rgb": "225, 29, 72",
      "--bg-deep": "#02040a",
      "--glass-bg": "rgba(15, 2, 8, 0.75)",
      "--glass-border": "rgba(225, 29, 72, 0.3)",
      "--text-main": "#fff",
      "--accent-glow": "rgba(192, 38, 211, 0.4)",
      "--surface-2": "#1a050d",
    },
    cyber: {
      "--primary": "#00f2ff",
      "--secondary": "#ff00e5",
      "--primary-rgb": "0, 242, 255",
      "--bg-deep": "#040404",
      "--glass-bg": "rgba(5, 5, 5, 0.85)",
      "--glass-border": "rgba(0, 242, 255, 0.4)",
      "--text-main": "#ffffff",
      "--accent-glow": "rgba(255, 0, 229, 0.4)",
      "--surface-2": "#0a0a0a",
    },
  };

  const selectedPalette = palettes[palette] || palettes.midnight;

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    const locale = i18n.language === "en" ? "en-US" : "pt-BR";
    return date.toLocaleString(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className={`business-v2 ${isVisible ? "ready" : ""}`} style={selectedPalette}>
      <div className="v2-background">
        <div className="v2-glow v2-glow-top"></div>
        <div className="v2-glow v2-glow-bottom"></div>
        <div className="v2-mesh"></div>
      </div>

      <div className="v2-content">
        <nav className="v2-nav">
          <Link to="/bots" className="v2-back-link">
            <span className="back-icon">←</span> {t("bot_details.hero.back_to_bots")}
          </Link>
          <div className="v2-nav-badges">
            {bot.verified && <span className="v2-badge v2-badge-verified">✓ {t("bot_details.business_v2.verified_bot")}</span>}
            {bot.certified && <span className="v2-badge v2-badge-certified">★ {t("bot_details.business_v2.certified_bot")}</span>}
            <span className="v2-badge v2-badge-business">{bot.apiTier === "premium" ? t("bot_details.business_v2.premium_badge") : t("bot_details.business_v2.badge")}</span>
          </div>
        </nav>

        <div className="v2-main-grid">
          {/* Sidebar Area */}
          <aside className="v2-sidebar">
            <section className="v2-identity-section">
              <div className="v2-avatar-wrapper">
                <div className="avatar-energy-rings">
                  <div className="ring ring-1"></div>
                  <div className="ring ring-2"></div>
                  <div className="ring ring-3"></div>
                </div>
                <img src={bot.avatar || `https://cdn.discordapp.com/embed/avatars/${parseInt(bot.discriminator || "0") % 5}.png`} alt={bot.name} className="v2-main-avatar" />
                <div className="v2-online-status"></div>
              </div>

              <div className="v2-info-stack">
                <h1 className="v2-bot-name">{bot.name}</h1>
                <p className="v2-bot-tagline">{bot.tagline || bot.shortDescription || bot.description}</p>

                <div className="v2-stats-matrix">
                  <div className="v2-stat-tile">
                    <span className="label">{t("bot_details.business_v2.servers")}</span>
                    <span className="value">{bot.servers?.toLocaleString() || 0}</span>
                  </div>
                  <div className="v2-stat-tile">
                    <span className="label">{t("bot_details.business_v2.users")}</span>
                    <span className="value">{bot.users?.toLocaleString() || 0}</span>
                  </div>
                  <div className="v2-stat-tile">
                    <span className="label">{t("bot_details.business_v2.votes")}</span>
                    <span className="value">{bot.votes?.toLocaleString() || 0}</span>
                  </div>
                  <div className="v2-stat-tile">
                    <span className="label">{t("bot_details.business_v2.monthly_votes")}</span>
                    <span className="value">{bot.monthlyVotes?.toLocaleString() || 0}</span>
                  </div>
                </div>

                <div className="v2-action-group">
                  <a href={bot.links.invite} target="_blank" rel="noopener noreferrer" className="v2-btn-primary">
                    <span>🚀 {t("bot_details.hero.actions.add_simple")}</span>
                    <div className="btn-shine"></div>
                  </a>
                  <button onClick={onVote} className="v2-btn-secondary">
                    <span>🗳️ {t("bot_details.hero.actions.vote_simple")}</span>
                  </button>
                  <button onClick={() => onDashboard && onDashboard()} className="v2-btn-dashboard">
                    <span>🎮 {t("bot_details.business_v2.dashboard_btn")}</span>
                  </button>
                </div>
              </div>

              <div className="v2-social-links">
                {bot.links.website && (
                  <a href={bot.links.website} target="_blank" rel="noreferrer" className="v2-social-icon" title={t("bot_details.business_v2.website")}>
                    🌐
                  </a>
                )}
                {bot.links.support && (
                  <a href={bot.links.support} target="_blank" rel="noreferrer" className="v2-social-icon" title={t("bot_details.business_v2.support")}>
                    💬
                  </a>
                )}
                {bot.links.github && (
                  <a href={bot.links.github} target="_blank" rel="noreferrer" className="v2-social-icon" title={t("bot_details.business_v2.github")}>
                    🐙
                  </a>
                )}
                {bot.links.twitter && (
                  <a href={bot.links.twitter} target="_blank" rel="noreferrer" className="v2-social-icon" title={t("bot_details.business_v2.twitter")}>
                    🐦
                  </a>
                )}
                {bot.links.instagram && (
                  <a href={bot.links.instagram} target="_blank" rel="noreferrer" className="v2-social-icon" title={t("bot_details.business_v2.instagram")}>
                    📸
                  </a>
                )}
                {bot.links.youtube && (
                  <a href={bot.links.youtube} target="_blank" rel="noreferrer" className="v2-social-icon" title={t("bot_details.business_v2.youtube")}>
                    📺
                  </a>
                )}
              </div>
            </section>

            <section className="v2-owner-card-mini">
              <div className="owner-card-content">
                <img src={bot.owner.avatar} alt={bot.owner.name} className="owner-avatar" />
                <div className="owner-details">
                  <span className="owner-sub">{t("bot_details.sidebar.dev_title")}</span>
                  <Link to={`/profile/${bot.owner.id}`} className="owner-name">
                    {bot.owner.name}
                  </Link>
                </div>
              </div>
            </section>
          </aside>

          {/* Detailed Content Columns */}
          <main className="v2-main-content">
            {/* Overview Card */}
            <section className="v2-glass-card v2-overview-card">
              <div className="v2-card-header">
                <h3 className="v2-card-title">{t("bot_details.tabs.overview")}</h3>
                <div className="v2-rating-pill">
                  <span className="star">★</span>
                  <strong>{bot.rating?.toFixed(1) || "0.0"}</strong>
                  <span className="separator"></span>
                  <span className="count">
                    {bot.totalReviews || 0} {t("bot_details.business_v2.reviews")}
                  </span>
                </div>
              </div>
              <div className="v2-card-body">
                <div className="v2-description-text markdown-body">
                  <ReactMarkdown>{bot.longDescription || bot.description}</ReactMarkdown>
                </div>
              </div>
            </section>

            {/* Categories & Tags Area - Moved up for better visibility */}
            {((bot.categories && bot.categories.length > 0) || (bot.tags && bot.tags.length > 0)) && (
              <section className="v2-glass-card v2-pills-card">
                <div className="v2-card-header">
                  <h3 className="v2-card-title">{t("bot_details.business_v2.categories_title")}</h3>
                </div>
                <div className="v2-card-body">
                  <div className="v2-pills-flow">
                    {bot.categories && bot.categories.length > 0 && (
                      <div className="pills-group">
                        {bot.categories.map((cat) => (
                          <span key={cat} className="v2-category-pill">
                            {t(`addBot.step3.categories.${cat}`, cat)}
                          </span>
                        ))}
                      </div>
                    )}

                    {bot.categories?.length > 0 && bot.tags?.length > 0 && <div className="pills-divider"></div>}

                    {bot.tags && bot.tags.length > 0 && (
                      <div className="pills-group tags">
                        {bot.tags.map((tag) => (
                          <span key={tag} className="v2-tag-chip">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </section>
            )}

            {/* Tech Grid */}
            <div className="v2-content-grid">
              <section className="v2-glass-card v2-tech-card">
                <div className="v2-card-header">
                  <h3 className="v2-card-title">{t("bot_details.business_v2.technical")}</h3>
                </div>
                <div className="v2-card-body">
                  <div className="v2-spec-list">
                    <div className="v2-spec-item">
                      <span className="spec-label">
                        <span className="spec-icon">⌨️</span>
                        {t("bot_details.business_v2.prefix")}
                      </span>
                      <code className="spec-value">{bot.prefix}</code>
                    </div>
                    <div className="v2-spec-item">
                      <span className="spec-label">
                        <span className="spec-icon">💎</span>
                        {t("bot_details.business_v2.shards")}
                      </span>
                      <span className="spec-value">{bot.shards || 1}</span>
                    </div>
                  </div>
                </div>
              </section>

              <section className="v2-glass-card v2-sync-card">
                <div className="v2-card-header">
                  <h3 className="v2-card-title">{t("bot_details.business_v2.sync_title")}</h3>
                </div>
                <div className="v2-card-body">
                  <div className="v2-spec-list">
                    <div className="v2-spec-item">
                      <span className="spec-label">
                        <span className="spec-icon">🔄</span>
                        {t("bot_details.business_v2.last_update")}
                      </span>
                      <span className="spec-value date">{formatDate(bot.statsUpdatedAt || bot.updatedAt)}</span>
                    </div>
                    <div className="v2-spec-item">
                      <span className="spec-label">
                        <span className="spec-icon">💓</span>
                        {t("bot_details.business_v2.last_signal")}
                      </span>
                      <span className="spec-value date">{formatDate(bot.lastHeartbeat)}</span>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </main>
        </div>

        {/* <footer className="v2-footer">
          <p>{t("bot_details.business_v2.footer_text")}</p>
          <div className="v2-certified-badge">
            <span className="check">✓</span>{" "}
            {t("bot_details.business_v2.certified_dev")}
          </div>
        </footer> */}
      </div>
    </div>
  );
}

export default BusinessBotView;
